import type { RelatedSectionKey, RelatedType } from './schemas/related.js';
import { PROGRAMME_UNIT, STORIES_PATH, storyPath, trackPath, TRACKS_PATH, wireframePath } from './routes.js';

/**
 * The entity rail's URL grammar (planning-app-information-architecture.md §4.4, `v3.9b`), shared
 * by the SPA, the API and the MCP server: the canonical page of an entity, the `?from=<type>:<id>`
 * every rail link carries (the target's crumbs show *from Story CR-5* as a trailing chip and *Back*
 * returns there), the owning list pre-filtered that *All N →* opens, and the filter a list page
 * reads back from its query string.
 */
// Plain tuples, not the zod enums' `.options`: the SPA reads them from every rail and list page, and a zod-backed constant would pull zod into those pages' chunks (the §4.5 budget on the decision page).
export const RELATED_TYPES = ['track', 'story', 'wireframe', 'document'] as const;
/** The rail's order (§4.1). */
export const RELATED_SECTIONS = ['tracks', 'stories', 'wireframes', 'documents', 'decisions', 'packages'] as const;
/** The list page that owns each section, and the type a *shares* filter on it refers to. */
export const SECTION_TYPE: Record<Exclude<RelatedSectionKey, 'decisions' | 'packages'>, RelatedType> = { tracks: 'track', stories: 'story', wireframes: 'wireframe', documents: 'document' };

const isType = (v: string): v is RelatedType => (RELATED_TYPES as readonly string[]).includes(v);

/** A document's id in a URL: the reader's slug (`architecture/x`), from either the slug or the repository path. */
export const documentId = (pathOrSlug: string): string => pathOrSlug.replace(/^docs\//, '').replace(/\.md$/, '');
/** The repository path of a document id. */
export const documentPathOf = (id: string): string => `docs/${documentId(id)}.md`;

/** The canonical page of an entity (§1.2: one URL per entity). */
export function entityPath(type: RelatedType, id: string): string {
  switch (type) {
    case 'track':
      return trackPath(id);
    case 'story':
      return storyPath(id);
    case 'wireframe':
      return wireframePath(id);
    case 'document':
      return `/docs/${documentId(id)
        .split('/')
        .map((s) => encodeURIComponent(s))
        .join('/')}`;
  }
}

export interface EntityRef {
  type: RelatedType;
  id: string;
}

/** The navigation context a page's query string may carry (§4.4): where the reader came from, and the track the page is read under. Neither is state of the page itself — a wireframe's deep-link state, a list's filters — so a page strips them before reading its own. */
export const CONTEXT_PARAMS = ['from', 'track'] as const;
export function stripContext(search: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams(search);
  for (const k of CONTEXT_PARAMS) out.delete(k);
  return out;
}
/** `href` with the context parameters of `search` carried over (a page rewriting its own state keeps where it was opened from). */
export function keepContext(href: string, search: URLSearchParams): string {
  const hash = href.indexOf('#');
  const base = hash >= 0 ? href.slice(0, hash) : href;
  const frag = hash >= 0 ? href.slice(hash) : '';
  const q = base.indexOf('?');
  const path = q >= 0 ? base.slice(0, q) : base;
  const params = new URLSearchParams(q >= 0 ? base.slice(q + 1) : '');
  for (const k of CONTEXT_PARAMS) {
    const v = search.get(k);
    if (v && !params.has(k)) params.set(k, v);
  }
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ''}${frag}`;
}

/** `?from=` as the rail writes it. */
export const fromParam = (ref: EntityRef): string => `${ref.type}:${ref.id}`;

/** `?from=` as a target page reads it: null for the older in-app-path and `doc:` forms, which the pages keep handling themselves. */
export function parseFrom(value: string | null | undefined): EntityRef | null {
  if (!value) return null;
  const i = value.indexOf(':');
  if (i <= 0) return null;
  const type = value.slice(0, i);
  const id = value.slice(i + 1);
  if (!isType(type) || id === '' || id.length > 200) return null;
  return { type, id };
}

/** `href` with one query parameter set (before any `#`). */
export function withParam(href: string, key: string, value: string): string {
  const hash = href.indexOf('#');
  const base = hash >= 0 ? href.slice(0, hash) : href;
  const frag = hash >= 0 ? href.slice(hash) : '';
  const q = base.indexOf('?');
  const path = q >= 0 ? base.slice(0, q) : base;
  const params = new URLSearchParams(q >= 0 ? base.slice(q + 1) : '');
  params.set(key, value);
  return `${path}?${params.toString()}${frag}`;
}
/** `href` with `from=<type>:<id>` added to its query. */
export const withFrom = (href: string, from: EntityRef): string => withParam(href, 'from', fromParam(from));
/** `href` with the `?track=` context a track page's links carry (§4.4); unchanged without a track. */
export const withTrack = (href: string, track: string | null | undefined): string => (track ? withParam(href, 'track', track) : href);

/** The `?related=<type>:<id>` a list page filters on; `/tracks?shares=`, `/stories?track=` and the like are the spellings IA §4.3 names. */
export const relatedParam = (ref: EntityRef): string => `related=${encodeURIComponent(fromParam(ref))}`;

/**
 * Where *All N →* of a section goes (§4.3): the owning list pre-filtered to the section's items.
 * Decisions and packages of a track are its tabs; of anything else, the Programme row's whole-
 * programme list and the Plan board, both filtered the same way.
 */
export function relatedListPath(section: RelatedSectionKey, ref: EntityRef): string {
  const { type, id } = ref;
  switch (section) {
    case 'tracks':
      return type === 'track' ? `${TRACKS_PATH}?shares=${encodeURIComponent(id)}` : `${TRACKS_PATH}?${relatedParam(ref)}`;
    case 'stories':
      return type === 'track' ? `${STORIES_PATH}?track=${encodeURIComponent(id)}` : `${STORIES_PATH}?${relatedParam(ref)}`;
    case 'wireframes':
      return type === 'track' ? `/wireframes?track=${encodeURIComponent(id)}` : `/wireframes?${relatedParam(ref)}`;
    case 'documents':
      return type === 'track' ? `/docs?track=${encodeURIComponent(id)}` : `/docs?${relatedParam(ref)}`;
    case 'decisions':
      return type === 'track' ? trackPath(id, 'decisions') : `${trackPath(PROGRAMME_UNIT, 'decisions')}?scope=all&${relatedParam(ref)}`;
    case 'packages':
      return type === 'track' ? trackPath(id, 'work') : `/plan?${relatedParam(ref)}`;
  }
}

/**
 * The related filter a list page carries, from `?related=<type>:<id>` or the named spellings:
 * `?shares=<id>` (the list's own type), `?track=<id>` on the stories, wireframes and documents
 * lists. `own` is the type the list shows, so `shares` resolves.
 */
export function relatedFilterOf(search: URLSearchParams, own: RelatedType): EntityRef | null {
  const explicit = parseFrom(search.get('related'));
  if (explicit) return explicit;
  const shares = search.get('shares');
  if (shares) return { type: own, id: shares };
  const track = search.get('track');
  if (track && own !== 'track') return { type: 'track', id: track };
  return null;
}
